import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useCMS } from '../../context/CMSContext';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { defaultFooterContent, defaultSocialLinks } from '../../data/pageContentDefaults';
import './PublicLayout.css';

/* ─────────────────────────────────────────────
   Inline SVG icons for social links
   (TikTok and X are not in Lucide React)
───────────────────────────────────────────── */
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.4a6.33 6.33 0 0 0-.79-.05A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
  </svg>
);

/* ─────────────────────────────────────────────
   TOP BAR
───────────────────────────────────────────── */
const TopBar = () => {
  const { settings } = useCMS();

  return (
    <div className="pl-topbar">
      <div className="pl-container">
        <div className="pl-topbar-inner">
          <div className="pl-topbar-left">
            {/* <a href="#">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Find a Store
            </a> */}
          </div>
          <div className="pl-topbar-right">
             <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/delivery">Delivery</Link>
            <Link to="/faq">FAQs</Link>
            <Link to="/reviews">Reviews</Link>
            {settings.b2bUrl && (
              <a href={settings.b2bUrl} target="_blank" rel="noopener noreferrer">Trade &amp; B2B</a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   ANNOUNCEMENT BAR  (CMS-driven with fallback)
───────────────────────────────────────────── */
const AnnouncementBar = () => {
  const { getActiveBanners } = useCMS();
  const [visible, setVisible] = useState(true);
  const announcements = getActiveBanners('announcement');

  if (!visible) return null;

  if (announcements.length === 0) {
    return (
      <div className="pl-announcement">
        <span className="pl-announcement-text">
        FREE PILLOWS, DUVETS &amp; MORE WORTH UP TO £880!
      </span>
      <Link to="/mattresses" className="pl-announcement-link">
        Shop Now
      </Link>
        <button
          className="pl-announcement-close"
          onClick={() => setVisible(false)}
          aria-label="Close announcement"
        >
          ×
        </button>
      </div>
    );
  }

  const ann = announcements[0];

  const announcementStyle = {};

  if (typeof ann?.backgroundColor === 'string' && ann.backgroundColor.trim()) {
    announcementStyle.backgroundColor = ann.backgroundColor.trim();
  }

  if (typeof ann?.textColor === 'string' && ann.textColor.trim()) {
    announcementStyle.color = ann.textColor.trim();
  }
  // Use CMS color if set; otherwise fall back to var(--violet) which follows the active theme
  // const annBg = ann.backgroundColor && ann.backgroundColor !== '#1a1f2e' ? ann.backgroundColor : 'var(--violet)';
  return (
    <div className="pl-announcement"
     style={Object.keys(announcementStyle).length ? announcementStyle : undefined}>
    <span className="pl-announcement-text">{ann.title}</span>
    {ann.buttonLink && (
      <Link to={ann.buttonLink} className="pl-announcement-link">
        {ann.buttonText || 'Shop Now'}
      </Link>
    )}
      <button
        className="pl-announcement-close"
        onClick={() => setVisible(false)}
        aria-label="Close announcement"
      >
        ×
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────
   HEADER SEARCH
───────────────────────────────────────────── */
const HeaderSearch = ({ className = 'pl-search', onSubmitted }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const term = query.trim();
    if (!term) return;
    navigate(`/mattresses?search=${encodeURIComponent(term)}`);
    setQuery('');
    if (onSubmitted) onSubmitted();
  };

  return (
    <form className={className} onSubmit={handleSubmit} role="search">
      <input
        className="pl-search-input"
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search products…"
        aria-label="Search products"
      />
      <button type="submit" className="pl-search-btn" aria-label="Search">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      </button>
    </form>
  );
};

/* ─────────────────────────────────────────────
   USER MENU DROPDOWN (backend integration)
───────────────────────────────────────────── */
const UserMenuDropdown = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        className="pl-header-icon"
        aria-label="Account menu"
        style={{ position: 'relative' }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="22" height="22" aria-hidden="true">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        {/* Small green dot indicating logged in */}
        <span style={{
          position: 'absolute', bottom: 2, right: 2,
          width: 8, height: 8, borderRadius: '50%',
          background: '#22c55e', border: '2px solid var(--white)',
        }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '0.5rem',
          backgroundColor: 'var(--white)',
          border: '1px solid var(--gray-200)',
          borderRadius: 10,
          boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
          minWidth: 200,
          zIndex: 1001,
          overflow: 'hidden'
        }}>
          <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid var(--gray-200)' }}>
            <p style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--navy)' }}>{user.name || user.username || 'User'}</p>
            <p style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{user.email}</p>
          </div>
          <div style={{ padding: '0.5rem 0' }}>
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', fontSize: '0.85rem', fontWeight: 500, color: 'var(--gray-700)', textDecoration: 'none' }}
            >
              Profile
            </Link>
            <Link
              to="/orders"
              onClick={() => setOpen(false)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', fontSize: '0.85rem', fontWeight: 500, color: 'var(--gray-700)', textDecoration: 'none' }}
            >
              Orders
            </Link>
            <Link
              to="/wishlist"
              onClick={() => setOpen(false)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', fontSize: '0.85rem', fontWeight: 500, color: 'var(--gray-700)', textDecoration: 'none' }}
            >
              Wishlist
            </Link>
          </div>
          <div style={{ borderTop: '1px solid var(--gray-200)', padding: '0.5rem 0' }}>
            <button
              onClick={() => { setOpen(false); onLogout(); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1rem',
                fontSize: '0.85rem',
                fontWeight: 500,
                color: '#ef4444',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left'
              }}
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN HEADER + NAVIGATION
───────────────────────────────────────────── */
const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const location                     = useLocation();
  const navigate                     = useNavigate();
  const { getNavCategories, getNavProducts, settings } = useCMS();
  const { itemCount }                = useCart();
  const { user, isAuthenticated, logout } = useAuth();

  const navCategories = getNavCategories();


  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  const getProductHref = (product) => `/mattresses/${product.slug}`;

  return (
    <header className={`pl-header${scrolled ? ' pl-header--scrolled' : ''}`}>
      <div className="pl-container">
        <div className="pl-header-inner">

          {/* ── Logo + desktop nav ── */}
          <div className="pl-header-left">
            <Link to="/" className="pl-logo">
              <img src={settings.logo || '/logo.png'} alt="Prime Mattresses" className="pl-logo-img" />
            </Link>
          </div>

          {/* Center: Navigation — driven by categories from CMS */}
          <nav className="pl-nav" aria-label="Main navigation">

            {navCategories.map((cat) => {
              const path = `/${cat.slug}`;
              const navProducts = getNavProducts(cat.key);
              return (
                <div key={cat.key} className="pl-nav-item pl-nav-item--mega">
                  <Link to={path} className={`pl-nav-link${isActive(path) ? ' pl-nav-link--active' : ''}`}>
                    {cat.name}
                  </Link>

                  <div className="pl-mega-menu" role="region" aria-label={`${cat.name} menu`}>
                    <div className={`pl-mega-grid${cat.key !== 'mattress' ? ' pl-mega-grid--2col' : ''}`}>

                      {/* Column 1 — Products */}
                      <div>
                        <div className="pl-mega-title">Shop by Type</div>
                        <ul className="pl-mega-links">
                          {navProducts.length > 0 ? navProducts.map((product) => (
                            <li key={product.id}>
                              <Link to={getProductHref(product)}>
                                {product.shortName || product.name}
                                {(product.navTag || product.badge) && (
                                  <span className={`pl-mega-badge${(product.navTag || product.badge) === 'New' ? ' pl-mega-badge--new' : ''}`}>
                                    {product.navTag || product.badge}
                                  </span>
                                )}
                              </Link>
                            </li>
                          )) : (
                            <li><Link to={path}>View all {cat.name}</Link></li>
                          )}
                        </ul>
                      </div>

                      {/* Column 2 — Shop by Size (mattresses only) */}
                      {cat.key === 'mattress' && (
                        <div>
                          <div className="pl-mega-title">Shop by Size</div>
                          <ul className="pl-mega-links">
                            <li><Link to="/mattresses?size=single">Single Mattresses</Link></li>
                            <li><Link to="/mattresses?size=small-double">Small Double Mattresses</Link></li>
                            <li><Link to="/mattresses?size=double">Double Mattresses</Link></li>
                            <li><Link to="/mattresses?size=king">King Size Mattresses</Link></li>
                            <li><Link to="/mattresses?size=super-king">Super King Mattresses</Link></li>
                            <li><Link to="/mattresses?size=eu-single">European Single</Link></li>
                            <li><Link to="/mattresses?size=eu-double">European Double</Link></li>
                            <li><Link to="/mattresses?size=eu-queen">European Queen</Link></li>
                          </ul>
                        </div>
                      )}

                      {/* Promo panel */}
                      <div>
                        <div className="pl-mega-promo">
                          <div className="pl-mega-promo-title">Which {cat.name.toLowerCase().replace(/s$/, '')} is right for you?</div>
                          <Link to="/quiz" className="pl-mega-promo-btn">Try Our Quiz →</Link>
                          <img
                            src="https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=400"
                            alt="Quiz"
                            className="pl-mega-promo-img"
                            loading="lazy"
                          />
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              );
            })}

          </nav>


          {/* ── Right icons ── */}
          <div className="pl-header-icons">
            <HeaderSearch />

            {/* User / Auth */}
            {isAuthenticated ? (
              <UserMenuDropdown user={user} onLogout={handleLogout} />
            ) : (
              <Link to="/login" className="pl-header-icon" aria-label="Account">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="22" height="22" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </Link>
            )}

            {/* Cart with badge */}
            <Link to="/cart" className="pl-header-icon" aria-label="Shopping bag" style={{ position: 'relative' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="22" height="22" aria-hidden="true">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              {itemCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: -4,
                  right: -6,
                  backgroundColor: 'var(--violet, #7c3aed)',
                  color: 'white',
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}>
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </Link>

            {/* Mobile burger */}
            <button
              className="pl-mobile-burger"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24" aria-hidden="true">
                  <path d="M3 12h18M3 6h18M3 18h18" />
                </svg>
              )}
            </button>
          </div>

        </div>
      </div>

      {/* ── Mobile drawer ── */}
      {mobileOpen && (
        <nav className="pl-mobile-menu" aria-label="Mobile navigation">
          <div className="pl-mobile-search">
            <HeaderSearch onSubmitted={() => setMobileOpen(false)} />
          </div>
          <Link to="/mattresses" className="pl-mobile-link">Mattresses</Link>
          <Link to="/pillows"    className="pl-mobile-link">Pillows</Link>
          <Link to="/duvets"     className="pl-mobile-link">Duvets</Link>
          <Link to="/toppers"    className="pl-mobile-link">Toppers</Link>
          <Link to="/about"      className="pl-mobile-link">About</Link>
          <Link to="/reviews"    className="pl-mobile-link">Reviews</Link>
          <Link to="/faq"        className="pl-mobile-link">FAQs</Link>
          <Link to="/contact"    className="pl-mobile-link">Contact</Link>
          <Link to="/delivery"   className="pl-mobile-link">Delivery</Link>
          {!isAuthenticated && (
            <>
              <Link to="/login"    className="pl-mobile-link">Login</Link>
              <Link to="/register" className="pl-mobile-link">Register</Link>
            </>
          )}
          {isAuthenticated && (
            <>
              <Link to="/profile"  className="pl-mobile-link">Profile</Link>
              <Link to="/orders"   className="pl-mobile-link">Orders</Link>
              <Link to="/wishlist" className="pl-mobile-link">Wishlist</Link>
              <Link to="/cart"     className="pl-mobile-link">Cart</Link>
              <button onClick={() => { setMobileOpen(false); handleLogout(); }} className="pl-mobile-link" style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', color: '#ef4444', font: 'inherit' }}>Logout</button>
            </>
          )}
        </nav>
      )}
    </header>
  );
};

/* ─────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────── */
const FooterColumnLink = ({ label, url }) => {
  if (!url) return <span>{label}</span>;
  if (/^https?:\/\//.test(url)) {
    return <a href={url} target="_blank" rel="noopener noreferrer">{label}</a>;
  }
  return <Link to={url}>{label}</Link>;
};

const Footer = () => {
  const { settings } = useCMS();
  const stored  = settings.footer;
  // Footer renders on every public page, so bad CMS content must not crash the site.
  const content = stored && typeof stored === 'object' ? stored : defaultFooterContent;
  const columns = Array.isArray(content.columns) ? content.columns : defaultFooterContent.columns;
  const social  = settings.socialLinks || defaultSocialLinks;

  const ttHref = social.tiktok    || '';
  const fbHref = social.facebook  || '';
  const igHref = social.instagram || '';
  const twHref = social.twitter   || '';
  const liHref = social.linkedin  || '';
  const logoSrc = content.logo || settings.logo;

  return (
    <footer className="pl-footer">
      <div className="pl-container">
        <div className="pl-footer-grid">

          {columns.map((column, i) => (
            <div key={i}>
              <h4 className="pl-footer-title">{column.title}</h4>
              <ul className="pl-footer-links">
                {(column.links || []).map((link, j) => (
                  <li key={j}><FooterColumnLink label={link.label} url={link.url} /></li>
                ))}
              </ul>
            </div>
          ))}

          {/* Logo + Social */}
          <div className="pl-footer-brand">
            <img src={logoSrc} alt="Prime Mattresses logo" className="pl-footer-logo" />
            <div className="pl-footer-social">
              {ttHref && (
                <a href={ttHref} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                  <TikTokIcon />
                </a>
              )}
              {fbHref && (
                <a href={fbHref} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <FacebookIcon />
                </a>
              )}
              {igHref && (
                <a href={igHref} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <InstagramIcon />
                </a>
              )}
              {twHref && (
                <a href={twHref} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                  <XIcon />
                </a>
              )}
              {liHref && (
                <a href={liHref} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <LinkedInIcon />
                </a>
              )}
            </div>
            <p className="pl-footer-country">{content.countryLabel}</p>
          </div>

        </div>

        <div className="pl-footer-bottom">
          <p className="pl-footer-copyright">
            {content.copyright.replace('{year}', new Date().getFullYear())}
          </p>
        </div>
      </div>
    </footer>
  );
};

/* ─────────────────────────────────────────────
   ROOT LAYOUT COMPONENT
   Order matches index.html North Star:
   TopBar → Header (sticky) → AnnouncementBar → <main> → Footer
───────────────────────────────────────────── */
const PublicLayout = () => (
  <div className="public-layout">
    <TopBar />
    <Navigation />
    <AnnouncementBar />
    <main>
      <Outlet />
    </main>
    <Footer />
  </div>
);

export default PublicLayout;

